import { useState, useCallback } from "react";
import {
  getContainerClientRegisteredInfo,
  getAzureBlob,
  saveContainerWeight,
} from "../../../../services/container";
import { getClientsList } from "../../../../services/client";
import { getWastecategoryList } from "../../../../services/waste";
import { getCameraId } from "../../../../shared/utils/utils";
import { apiWeightToTonnes } from "../utils/utils";
import { resolveClientRegisteredInfoRange } from "../utils/characterizationViewMode";
import moment from "moment";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export const useClientData = ({
  container,
  superContainer,
  historyMaterial,
  historyId,
  period,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [clientName, setClientName] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientCode, setClientCode] = useState("");
  const [weight, setWeight] = useState("");
  const [wasteCategoryName, setWasteCategoryName] = useState("");

  const [clientList, setClientList] = useState([]);
  const [wasteCategoryList, setWasteCategoryList] = useState([]);
  const [corporationClients, setCorporationClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);

  const resetFormFields = useCallback(() => {
    setClientName("");
    setClientAddress("");
    setClientId("");
    setClientCode("");
    setWeight("");
    setWasteCategoryName("");
  }, []);

  const fetchClients = useCallback(async () => {
    const debug =
      process.env.REACT_APP_CHARACTERIZATION2_DEBUG === "true" ||
      process.env.REACT_APP_CHARACTERIZATION2_DEBUG === "1";
    // Si déjà chargé une fois (même 0 client), ne pas relancer la requête
    if (clientsLoaded) {
      return clientList;
    }
    try {
      const response = await getClientsList();
      const clients = response?.data?.data ?? [];

      const deduplicatedClients = clients.reduce(
        (acc, client) => {
          const key = `${client.name}-${client.address}`;
          if (!acc.keys.has(key)) {
            acc.keys.add(key);
            acc.items.push(client);
          }
          return acc;
        },
        { keys: new Set(), items: [] },
      ).items;
      setClientList(deduplicatedClients);
      setCorporationClients(deduplicatedClients);
      setClientsLoaded(true);
      if (debug) {
        // eslint-disable-next-line no-console
        console.log(
          "[char2] useClientData.fetchClients loaded",
          deduplicatedClients.length,
          "clients",
        );
      }
      return deduplicatedClients;
    } catch (error) {
      console.error("Échec du chargement des clients:", error);
      setClientsLoaded(true);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientsLoaded, clientList]);

  const fetchWasteCategories = useCallback(async () => {
    const debug =
      process.env.REACT_APP_CHARACTERIZATION2_DEBUG === "true" ||
      process.env.REACT_APP_CHARACTERIZATION2_DEBUG === "1";
    // Éviter de recharger si déjà présent
    if (wasteCategoryList.length > 0) {
      return;
    }
    try {
      const response = await getWastecategoryList({
        keywordFilter: "",
        listUnCodeFullFilter: {},
        listUnCodeEmptyFilter: {},
        listWasteCodeFullFilter: {},
        listWasteCodeEmptyFilter: {},
        sortByField: "label",
        orderSortByField: "1",
      });
      const list = response?.data?.data ?? [];
      setWasteCategoryList(list);
      if (debug) {
        // eslint-disable-next-line no-console
        console.log(
          "[char2] useClientData.fetchWasteCategories loaded",
          list.length,
          "waste categories",
        );
      }
    } catch (error) {
      console.error("Échec du chargement des catégories de déchets:", error);
      toast.error(t("Erreur lors du chargement des catégories de déchets"));
    }
  }, [t, wasteCategoryList.length]);

  const loadClientData = useCallback(
    async (clientsOverride = null) => {
      if (!container?._id || !historyMaterial || historyMaterial.length === 0) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const range = resolveClientRegisteredInfoRange({
          period,
          historyMaterial,
          historyId,
        });
        if (!range) {
          resetFormFields();
          setIsLoading(false);
          return;
        }
        const containerId =
          superContainer && superContainer._id !== undefined
            ? superContainer._id
            : container._id;
        const response = await getContainerClientRegisteredInfo(
          range,
          containerId,
        );
        const clientsToUse = clientsOverride ?? corporationClients;
        if (response?.data && Object.keys(response.data).length > 0) {
          const client = clientsToUse.find(
            (item) => item._id === response?.data?.mongoClientId,
          );
          if (client) {
            setClientName(client.name);
            setClientAddress(client.address);
            setClientId(response?.data?.mongoClientId);
          } else {
            resetFormFields();
          }
          setClientCode(
            response?.data?.client
              ? JSON.parse(response?.data?.client).code
              : "",
          );
          setWeight(
            (() => {
              const tonnes = apiWeightToTonnes(response?.data?.weight);
              return tonnes != null ? String(tonnes) : "";
            })(),
          );
          setWasteCategoryName(response?.data?.waste_category || "");
        } else {
          resetFormFields();
        }
      } catch (error) {
        console.error("Échec du chargement des données client:", error);
        resetFormFields();
      } finally {
        setIsLoading(false);
      }
    },
    [
      container?._id,
      superContainer,
      historyMaterial,
      historyId,
      period,
      corporationClients,
      resetFormFields,
    ],
  );

  const saveClientData = async (overrides = {}) => {
    const currentContainer = container;
    const currentHistoryMaterial = historyMaterial;
    const currentHistoryId = historyId;
    const currentWeight =
      overrides.weight !== undefined ? overrides.weight : weight;
    const currentClientName =
      overrides.clientName !== undefined ? overrides.clientName : clientName;
    const currentClientCode =
      overrides.clientCode !== undefined ? overrides.clientCode : clientCode;
    const currentClientAddress =
      overrides.clientAddress !== undefined
        ? overrides.clientAddress
        : clientAddress;
    const currentWasteCategoryName =
      overrides.wasteCategoryName !== undefined
        ? overrides.wasteCategoryName
        : wasteCategoryName;
    const currentClientId =
      overrides.clientId !== undefined ? overrides.clientId : clientId;
    if (
      !currentContainer?._id ||
      !currentHistoryMaterial ||
      currentHistoryMaterial.length === 0
    ) {
      toast.error(t("Données manquantes pour la sauvegarde"));
      return false;
    }
    const currentHistory = currentHistoryMaterial[currentHistoryId];
    const historyTime =
      currentHistory?._time ?? currentHistoryMaterial[0]?._time;
    if (!historyTime || typeof historyTime !== "string") {
      toast.error(t("Données manquantes pour la sauvegarde"));
      return false;
    }
    const getImageUrlFromHistoryItem = (h) =>
      h?.image_url ||
      (Array.isArray(h?.image_urls) && h.image_urls.length > 0
        ? h.image_urls[0]
        : null);

    setIsSaving(true);
    try {
      const imageUrl =
        getImageUrlFromHistoryItem(currentHistory) ||
        (currentHistoryMaterial && currentHistoryMaterial.length > 0
          ? currentHistoryMaterial.map(getImageUrlFromHistoryItem).find(Boolean)
          : null) ||
        currentContainer?.image_url ||
        "";
      const cameraId = getCameraId(imageUrl || "");
      if (!cameraId) {
        toast.error(t("Container data incomplete (image)"));
        setIsSaving(false);
        return false;
      }
      const containerId =
        superContainer && superContainer._id !== undefined
          ? superContainer._id
          : currentContainer._id;
      const timestamp = String(historyTime).replace(/:/g, "_").slice(0, -1);
      const blobName = `camera-ak_${cameraId}/output-client/${timestamp}-camera-ak_${cameraId}-${containerId}-client.json`;
      const clientData = {
        name: currentClientName || "",
        code: currentClientCode || "",
        address: currentClientAddress || "",
      };

      // Même convention que characterisation v1 : utilisateur saisit tonnes, on envoie kg
      const weightInKg =
        parseFloat(String(currentWeight).replace(",", ".")) * 1000;

      if (Number.isNaN(weightInKg) || weightInKg < 0) {
        toast.error(t("Le poids doit être un nombre valide"));
        setIsSaving(false);
        return false;
      }
      let existingComments = JSON.stringify([]);
      try {
        const azureUrl = process.env.REACT_APP_AZURE_URL || "";
        const blobUrl = `${azureUrl}${blobName}`;
        const azureResponse = await getAzureBlob({ blobUrl });
        const existing = azureResponse?.data;
        if (existing && existing.comments != null) {
          existingComments =
            typeof existing.comments === "string"
              ? existing.comments
              : JSON.stringify(existing.comments);
        }
      } catch {
        // Blob peut ne pas exister encore, on envoie un tableau vide
      }
      await saveContainerWeight({
        id: containerId,
        weight: weightInKg,
        time: historyTime,
        modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
        client: JSON.stringify(clientData),
        waste_category: currentWasteCategoryName ?? "",
        blobName: blobName,
        mongoClientId: currentClientId || undefined,
        comments: existingComments,
      });
      toast.success(t("Données sauvegardées avec succès"));
      return true;
    } catch (error) {
      console.error("Échec de la sauvegarde:", error);
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      toast.error(
        message
          ? `${t("Échec de la sauvegarde des données")}: ${message}`
          : t("Échec de la sauvegarde des données"),
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };
  return {
    clientName,
    clientAddress,
    clientId,
    clientCode,
    weight,
    wasteCategoryName,
    setClientName,
    setClientAddress,
    setClientId,
    setClientCode,
    setWeight,
    setWasteCategoryName,
    clientList,
    wasteCategoryList,
    corporationClients,
    isLoading,
    isSaving,
    fetchClients,
    fetchWasteCategories,
    loadClientData,
    saveClientData,
    resetFormFields,
  };
};
